import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { downloadOfferPdf } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { statusInfo, daysWaiting, historyLabel } from "@/lib/status";
import OfferDocument from "@/components/OfferDocument";
import { euro } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Download, Link2, Pencil, Trophy, XCircle, RotateCcw, Clock, History, FileSignature } from "lucide-react";
import { toast } from "sonner";

export default function OfferDetail() {
  const { offerId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [offer, setOffer] = useState(null);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/offers/${offerId}`);
        setOffer(data);
      } catch {
        toast.error("Offerta non trovata");
        navigate("/");
      }
    })();
  }, [offerId]);

  const copyLink = () => {
    const link = `${window.location.origin}/offerta/${offer.share_token}`;
    navigator.clipboard.writeText(link);
    toast.success("Link copiato negli appunti");
  };

  const changeStatus = async (status) => {
    try {
      const { data } = await api.patch(`/offers/${offerId}/status`, { status });
      setOffer(data);
      toast.success("Stato aggiornato");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore");
    }
  };

  const pdf = async () => {
    setDownloading(true);
    try {
      await downloadOfferPdf(offerId, offer.client_name);
    } catch {
      toast.error("Errore nel download del PDF");
    } finally {
      setDownloading(false);
    }
  };

  if (!offer) return <div className="p-10 text-muted-foreground">Caricamento...</div>;

  const si = statusInfo(offer.status);
  const isOwner = user?.role === "sales" && offer.agent_id === user.id;
  const history = [...(offer.history || [])].reverse();

  return (
    <div className="p-6 lg:p-10 max-w-5xl mx-auto">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-6 no-print">
        <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-muted-foreground hover:text-navy font-semibold transition-colors duration-200">
          <ArrowLeft size={18} /> Indietro
        </button>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => navigate(`/configuratore/${offerId}`)} data-testid="edit-offer-button" className="gap-2 font-bold">
            <Pencil size={16} /> Modifica
          </Button>
          <Button variant="outline" onClick={copyLink} data-testid="copy-link-button" className="gap-2 font-bold">
            <Link2 size={16} /> Copia link
          </Button>
          {user?.contracts_enabled && offer.status === "won" && (
            <Button variant="outline" onClick={() => navigate(`/offerte/${offerId}/contratto`)} data-testid="open-contract-button" className="gap-2 font-bold border-primary text-primary hover:bg-primary/10">
              <FileSignature size={16} /> Contratto
            </Button>
          )}
          <Button onClick={pdf} disabled={downloading} data-testid="download-pdf-button" className="gap-2 font-bold">
            <Download size={16} /> {downloading ? "..." : "Scarica PDF"}
          </Button>
        </div>
      </div>

      {/* Status bar */}
      <div className="bg-white border border-border rounded-xl p-5 mb-6 no-print flex flex-wrap items-center justify-between gap-4">
        <div className="flex flex-wrap items-center gap-x-8 gap-y-3">
          <div>
            <p className="text-xs uppercase tracking-widest text-muted-foreground font-bold">Stato</p>
            <span data-testid="offer-status-badge" className={`inline-block mt-1 px-3 py-1 rounded-full text-sm font-bold border ${si.cls}`}>{si.label}</span>
          </div>
          <div>
            <p className="text-xs uppercase tracking-widest text-muted-foreground font-bold">
              {offer.status === "won" ? "Conversione in" : "Giorni in attesa"}
            </p>
            <p className="mt-1 font-extrabold text-navy flex items-center gap-1.5" data-testid="offer-days-waiting">
              <Clock size={16} className="text-muted-foreground" /> {daysWaiting(offer)} giorni
            </p>
          </div>
          {offer.won_at && (
            <div>
              <p className="text-xs uppercase tracking-widest text-muted-foreground font-bold">Data vendita</p>
              <p className="mt-1 font-extrabold text-primary">{offer.won_at.slice(0, 10)}</p>
            </div>
          )}
        </div>
        {isOwner && (
          <div className="flex flex-wrap gap-2">
            {offer.status !== "won" && (
              <Button onClick={() => changeStatus("won")} data-testid="mark-won-button" className="gap-2 font-bold">
                <Trophy size={16} /> Segna Vinta
              </Button>
            )}
            {offer.status !== "lost" && (
              <Button variant="outline" onClick={() => changeStatus("lost")} data-testid="mark-lost-button" className="gap-2 font-bold text-destructive border-destructive/40 hover:bg-destructive/10">
                <XCircle size={16} /> Segna Persa
              </Button>
            )}
            {offer.status !== "open" && (
              <Button variant="outline" onClick={() => changeStatus("open")} data-testid="reopen-button" className="gap-2 font-bold">
                <RotateCcw size={16} /> Riapri
              </Button>
            )}
          </div>
        )}
      </div>

      <div className="bg-white border border-border rounded-2xl p-6 md:p-10">
        <OfferDocument offer={offer} />
      </div>

      {offer.totals?.partner_monthly !== undefined && (
        <div className="bg-white border border-dashed border-navy/30 rounded-2xl p-6 md:p-8 mt-6 no-print" data-testid="agency-margin-panel">
          <h3 className="font-extrabold text-navy flex items-center gap-2 mb-1">Margine agenzia</h3>
          <p className="text-sm text-muted-foreground mb-5">Riservato ad agenzia e Bonsai — mai visibile al cliente.</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="rounded-xl border border-border p-4">
              <p className="text-xs uppercase tracking-widest text-muted-foreground font-bold">Prezzo cliente</p>
              <p className="mt-1 text-xl font-extrabold text-navy">{euro(offer.totals.monthly_final != null ? offer.totals.monthly_final : offer.totals.monthly_net)}<span className="text-xs text-muted-foreground font-semibold">/mese</span></p>
              {offer.totals.oneoff_net > 0 && <p className="text-sm text-muted-foreground">+ {euro(offer.totals.oneoff_net)} una tantum</p>}
            </div>
            <div className="rounded-xl border border-border p-4">
              <p className="text-xs uppercase tracking-widest text-muted-foreground font-bold">Prezzo partner (a Bonsai)</p>
              <p className="mt-1 text-xl font-extrabold text-navy" data-testid="detail-partner-monthly">{euro(offer.totals.partner_monthly)}<span className="text-xs text-muted-foreground font-semibold">/mese</span></p>
              {offer.totals.partner_oneoff > 0 && <p className="text-sm text-muted-foreground">+ {euro(offer.totals.partner_oneoff)} una tantum</p>}
            </div>
            <div className="rounded-xl border border-border p-4">
              <p className="text-xs uppercase tracking-widest text-muted-foreground font-bold">Sconto accollato dall'agenzia</p>
              <p className="mt-1 text-xl font-extrabold text-primary" data-testid="detail-agency-discount">{offer.totals.agency_discount > 0 ? "− " + euro(offer.totals.agency_discount) + "/mese" : "—"}</p>
            </div>
            <div className="rounded-xl border border-border p-4">
              <p className="text-xs uppercase tracking-widest text-muted-foreground font-bold">Margine agenzia</p>
              <p className={`mt-1 text-xl font-extrabold ${offer.totals.agency_margin_monthly < 0 ? "text-destructive" : "text-primary"}`} data-testid="detail-agency-margin">{euro(offer.totals.agency_margin_monthly)}<span className="text-xs text-muted-foreground font-semibold">/mese</span></p>
              {offer.totals.oneoff_net > 0 && <p className="text-sm text-muted-foreground">+ {euro(offer.totals.agency_margin_oneoff)} una tantum</p>}
            </div>
          </div>
        </div>
      )}

      {/* History */}
      <div className="bg-white border border-border rounded-2xl p-6 md:p-8 mt-6 no-print">
        <h3 className="font-extrabold text-navy flex items-center gap-2 mb-4"><History size={18} className="text-primary" /> Cronologia</h3>
        <ol className="space-y-4">
          {history.length === 0 && <p className="text-sm text-muted-foreground">Nessun evento.</p>}
          {history.map((h, i) => (
            <li key={i} data-testid={`history-event-${i}`} className="flex gap-3">
              <div className="w-2 h-2 rounded-full bg-primary mt-2 shrink-0" />
              <div>
                <p className="font-semibold text-navy text-sm">{historyLabel(h.type)}</p>
                <p className="text-xs text-muted-foreground">{(h.at || "").replace("T", " ").slice(0, 16)} · {h.by}</p>
              </div>
            </li>
          ))}
        </ol>
      </div>
    </div>
  );
}

